/**
 * Role-aware access control · cohort/tariff model (slot_role_management · 10 JUL
 * 2026 · Genie · generalizes slot_progression_plan_quiz · 2 JUN 2026).
 *
 * Layers user tiers on top of the linear sprint-progression rules. Every tier is
 * a cap on the highest sprint ord a user may open — the cap table + helpers are
 * the SINGLE SOURCE OF TRUTH in `shared/access/roles.ts`; this module only wires
 * them to the client UX (locks · read-only peek · upsell · dev toggle).
 *
 *   admin      — DEV unlock-all toggle visible; standard linear otherwise, uncapped.
 *   pro        — S01–S19 by standard linear rules; no demo/peek (top paid tier).
 *   start / manualplus — paid, capped at S07 / S13; a single read-only teaser of
 *                the next tier's first sprint; upsell beyond the cap.
 *   trial      — S01–S04 normally (progress saved); S05+ hard-locked, S02–S05
 *                peekable read-only (demo-round preview). Upgradeable.
 *   free       — S01 only (лид-магнит); S02 peekable; upsell beyond.
 *
 * Role lives in public.profiles.role → User.role. Absent ⇒ 'trial' (least
 * privilege). NOTE: client-side gating is UX, not security — the real gate is
 * Supabase RLS + the edge `content`/`quiz` fns (shared `isContentEntitled`).
 */

import type { Progress, User, UserRole } from '@shared/schemas';
import { SPRINTS } from '@shared/data/sprints';
import { DEFAULT_ROLE, isPaidRole, maxOrdForRole } from '@shared/access/roles';
import { isSprintLocked } from './sprint-progression';

/**
 * Highest sprint ord a `trial` user can OPEN for real. Kept as a named export for
 * back-compat (tests + call sites); DERIVED from the canonical role→cap table.
 */
export const TRIAL_MAX_SPRINT_ORD = maxOrdForRole('trial');

/** Highest sprint ord a `trial` user can PEEK (cap + 1 · the «посмотреть что дальше»
 *  bonus). Derived so it tracks the trial cap automatically. */
export const TRIAL_PEEK_MAX_ORD = maxOrdForRole('trial') + 1;

/** Effective access tier · absent/unknown role ⇒ 'trial' (least privilege). */
export function roleOf(user: User | null | undefined): UserRole {
  return user?.role ?? DEFAULT_ROLE;
}

const ordOf = (sprintId: string): number | undefined =>
  SPRINTS.find((s) => s.id === sprintId)?.ord;

/**
 * Role-aware sprint lock — superset of `isSprintLocked` (linear progression +
 * admin dev-unlock). A sprint past the role's tier ceiling (`maxOrdForRole`) is
 * hard-locked regardless of progress or the dev flag; within the ceiling the
 * standard linear rules apply. Admin's ceiling is Infinity → linear rules only.
 */
export function isSprintLockedForUser(
  progress: Progress,
  sprintId: string,
  role: UserRole,
): boolean {
  const ord = ordOf(sprintId);
  if (ord === undefined) return true;
  if (ord > maxOrdForRole(role)) return true; // beyond the paid ceiling
  return isSprintLocked(progress, sprintId);
}

/**
 * Can the user PEEK this sprint in read-only demo? Never for admin.
 *   · free / trial (unpaid) — preview-ahead within the demo range: any sprint
 *     that is currently locked for them and within ord ≤ cap+1. (S01 opens from
 *     the start so it's never a peek; S{cap+1} is the bonus «что дальше».)
 *   · start / manualplus / pro (paid) — a single read-only teaser of the FIRST
 *     sprint of the next tier (exactly ord === cap+1), as an upsell. `pro`'s
 *     cap+1 = S20 doesn't exist yet → pro never peeks.
 */
export function canPeekSprint(progress: Progress, sprintId: string, role: UserRole): boolean {
  if (role === 'admin') return false;
  const ord = ordOf(sprintId);
  if (ord === undefined) return false;
  const cap = maxOrdForRole(role);
  if (isPaidRole(role)) {
    // Paid tiers only tease the next tier's opening sprint — no within-range demo.
    return ord === cap + 1 && isSprintLockedForUser(progress, sprintId, role);
  }
  // free / trial: preview-ahead within the demo range (ord ≤ cap+1).
  if (ord > cap + 1) return false;
  return isSprintLockedForUser(progress, sprintId, role);
}

/**
 * Should this sprint show the «купить полный курс / следующий тариф» upsell? For
 * any non-admin role, every sprint beyond their tier ceiling (ord > cap). The
 * boundary sprint (cap+1) also shows the peek button; higher ones are visually
 * locked. admin: never. pro: never (no sprint beyond S19).
 *
 * NOTE (copy follow-up): the upsell wording currently says «полный курс» — for a
 * `start`/`manualplus` user the correct CTA is «повысить тариф до следующего».
 * The gating boolean is correct; the copy nuance is a marketing-polish TODO.
 */
export function showsUpsell(sprintId: string, role: UserRole): boolean {
  if (role === 'admin') return false;
  const ord = ordOf(sprintId);
  return ord !== undefined && ord > maxOrdForRole(role);
}

/** DEV unlock-all toggle is admin-only. */
export function isDevToggleVisible(role: UserRole): boolean {
  return role === 'admin';
}
